/* URBRAND Cart Page — v5.9.41 */
(() => {
  'use strict';

  const CART_TERMS_KEY = 'urbrand:cart-terms:v1';
  const CART_TERMS_LEGACY_KEY = 'urbrand:cart-terms';

  const controllers = new WeakMap();
  // Aborting a POST does not cancel a mutation already received by Shopify.
  let mutationQueue = Promise.resolve();
  let cartRevision = 0;
  const debounce = (fn, wait = 450) => {
    let timer;
    let pending;
    const flush = () => {
      window.clearTimeout(timer);
      if (!pending) return;
      const args = pending;
      pending = null;
      return fn(...args);
    };
    const delayed = (...args) => {
      pending = args;
      window.clearTimeout(timer);
      timer = window.setTimeout(flush, wait);
    };
    delayed.flush = flush;
    return delayed;
  };

  const text = (key, fallback) => window.URB?.strings?.[key] || fallback;

  const safeJson = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || text('cartUpdateError', 'Não foi possível atualizar o carrinho.'));
    return data;
  };

  const localeRoute = (suffix) => {
    const root = window.Shopify?.routes?.root || window.URB?.routes?.root || '/';
    return `${root.replace(/\/$/, '')}/cart/${suffix}`.replace(/\/+/g, '/');
  };

  class CartPage {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.form = root.querySelector('.cart-page-form');
      this.live = root.querySelector('[data-cart-live]');
      this.abortController = null;
      this.noteSave = debounce((value) => this.updateCart({ note: value }, false), 650);
      this.pendingAttributes = {};
      this.attributeTextSave = debounce(() => {
        const attributes = this.pendingAttributes;
        this.pendingAttributes = {};
        return this.updateCart({ attributes }, false);
      }, 500);
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.init();
    }

    init() {
      if (this.root.dataset.cartInitialized === 'true') return;
      this.root.dataset.cartInitialized = 'true';
      this.root.addEventListener('click', this.onClick);
      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('input', this.onInput);
      this.root.addEventListener('keydown', this.onKeydown);
      this.form?.addEventListener('submit', this.onSubmit);
      this.restoreTerms();
      this.syncQuantityButtons();
    }

    destroy() {
      this.noteSave.flush();
      this.attributeTextSave.flush();
      this.abortController?.abort();
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('input', this.onInput);
      this.root.removeEventListener('keydown', this.onKeydown);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.root.removeAttribute('data-cart-initialized');
    }

    announce(message) {
      if (!this.live || !message) return;
      this.live.textContent = '';
      window.requestAnimationFrame(() => { this.live.textContent = message; });
    }

    saveAttribute(key, value) {
      this.pendingAttributes[key] = value;
      this.attributeTextSave();
    }

    setLoading(loading, line = null) {
      this.root.classList.toggle('is-loading', loading);
      this.root.setAttribute('aria-busy', String(loading));
      if (line) line.classList.toggle('is-busy', loading);
      if (line) line.querySelectorAll('button,input,a').forEach((node) => {
        if ('disabled' in node) node.disabled = loading;
        node.setAttribute('aria-disabled', String(loading));
      });
      if (line && !loading) this.syncQuantityButtons(line);
    }

    syncQuantityButtons(scope = this.root) {
      scope.querySelectorAll('[data-cart-quantity-input]').forEach((input) => {
        const control = input.closest('[data-cart-quantity-control]');
        const down = control?.querySelector('[data-cart-quantity-step="down"]');
        const up = control?.querySelector('[data-cart-quantity-step="up"]');
        const value = Number(input.value || 0);
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Infinity;
        const step = Number(input.step || 1);
        if (down) down.disabled = value <= min;
        if (up) up.disabled = value + step > max;
      });
    }

    request(endpoint, payload, options = {}) {
      const operation = mutationQueue.catch(() => {}).then(() => this.performRequest(endpoint, payload, options));
      mutationQueue = operation;
      return operation;
    }

    async performRequest(endpoint, payload, { rerender = true, focus = null } = {}) {
      cartRevision += 1;
      const body = { ...payload };
      if (rerender && this.sectionId) {
        body.sections = [this.sectionId];
        body.sections_url = window.location.pathname + window.location.search;
      }
      const response = await fetch(localeRoute(endpoint), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const cart = await safeJson(response);
      if (rerender) {
        this.updateHeaderCount(cart.item_count || 0);
        const currentRoot = document.getElementById(this.root.id);
        const current = currentRoot && controllers.get(currentRoot);
        if (current) current.render(cart, focus);
      }
      return cart;
    }

    updateHeaderCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = String(count);
        node.hidden = count === 0;
      });
    }

    render(cart, focus) {
      const html = cart.sections?.[this.sectionId];
      if (!html) {
        window.location.reload();
        return;
      }
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const fresh = documentFragment.querySelector('[data-cart-section]');
      if (!fresh) {
        window.location.reload();
        return;
      }
      const parent = this.root.parentElement;
      this.destroy();
      this.root.replaceWith(fresh);
      init(parent || document);
      const newRoot = document.getElementById(fresh.id);
      if (!newRoot) return;
      const newController = controllers.get(newRoot);
      if (focus?.type === 'quantity') {
        const target = newRoot.querySelector(`[data-cart-line-index="${focus.line}"] [data-cart-quantity-input]`);
        target?.focus({ preventScroll: true });
      } else if (focus?.type === 'discount') {
        newRoot.querySelector('[data-cart-discount-input]')?.focus({ preventScroll: true });
      } else if (focus?.type === 'heading') {
        const heading = newRoot.querySelector('h1');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
      }
      newController?.announce(focus?.message || text('cartUpdated', 'Carrinho atualizado.'));
    }

    async changeLine(key, quantity, lineIndex, line) {
      if (!key) return;
      this.setLoading(true, line);
      try {
        await this.request('change.js', {
          id: String(key),
          quantity: Math.max(0, Number.parseInt(quantity, 10) || 0)
        }, {
          focus: quantity === 0
            ? { type: 'heading', message: text('cartItemRemoved', 'Item removido do carrinho.') }
            : { type: 'quantity', line: lineIndex, message: text('cartQuantityUpdated', 'Quantidade atualizada.') }
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.setLoading(false, line);
          window.URBToast?.(error.message);
          await this.refreshFromServer(error.message);
        }
      }
    }

    async updateCart(payload, rerender = false, focus = null) {
      try {
        return await this.request('update.js', payload, { rerender, focus });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.announce(error.message);
          window.URBToast?.(error.message);
        }
        return null;
      }
    }

    async refreshFromServer(message = '') {
      const revision = ++cartRevision;
      this.setLoading(true);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(text('cartSyncError', 'Não foi possível sincronizar o carrinho.'));
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (revision !== cartRevision || !this.root.isConnected) return;
        const fresh = documentFragment.querySelector('[data-cart-section]');
        if (!fresh) throw new Error(text('cartSyncError', 'Não foi possível sincronizar o carrinho.'));
        const parent = this.root.parentElement;
        this.destroy();
        this.root.replaceWith(fresh);
        init(parent || document);
        if (message) controllers.get(fresh)?.announce(message);
      } catch (error) {
        this.setLoading(false);
        this.announce(error.message);
      }
    }

    async clearCart() {
      this.setLoading(true);
      try {
        await this.request('clear.js', {}, { focus: { type: 'heading', message: text('cartCleared', 'Carrinho esvaziado.') } });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.setLoading(false);
          this.announce(error.message);
        }
      }
    }

    async applyDiscount(button) {
      const input = this.root.querySelector('[data-cart-discount-input]');
      const message = this.root.querySelector('[data-cart-discount-message]');
      const code = input?.value.trim();
      if (!code) {
        if (message) message.textContent = text('discountRequired', 'Informe um código de desconto.');
        input?.focus();
        return;
      }
      button.disabled = true;
      if (message) message.textContent = text('discountApplying', 'Aplicando…');
      try {
        await this.request('update.js', { discount: code }, {
          focus: { type: 'discount', message: text('discountProcessed', 'Cupom processado. Confira o resumo do pedido.') }
        });
      } catch (error) {
        button.disabled = false;
        if (message) message.textContent = error.message;
      }
    }

    restoreTerms() {
      const terms = this.root.querySelector('[data-cart-terms]');
      if (!terms || terms.checked) return;
      try {
        const storedTerms = sessionStorage.getItem(CART_TERMS_KEY) || sessionStorage.getItem(CART_TERMS_LEGACY_KEY);
        terms.checked = storedTerms === '1' || storedTerms === 'accepted';
      } catch { /* Optional storage. */ }
      this.updateCheckoutState();
    }

    updateCheckoutState() {
      const terms = this.root.querySelector('[data-cart-terms]');
      const button = this.root.querySelector('[data-cart-checkout-button]');
      if (!terms || !button) return;
      const blocked = terms.required && !terms.checked;
      button.disabled = false;
      button.setAttribute('aria-disabled', String(blocked));
      button.classList.toggle('is-disabled', blocked);
    }

    validateTerms() {
      const terms = this.root.querySelector('[data-cart-terms]');
      const message = this.root.querySelector('[data-cart-terms-message]');
      if (!terms || !terms.required || terms.checked) return true;
      if (message) message.textContent = window.URB?.strings?.acceptTerms || 'Aceite os termos para continuar.';
      terms.focus();
      return false;
    }

    onClick(event) {
      const step = event.target.closest('[data-cart-quantity-step]');
      if (step) {
        event.preventDefault();
        const line = step.closest('[data-cart-line]');
        const input = line?.querySelector('[data-cart-quantity-input]');
        if (!line || !input) return;
        const increment = Number(input.step || 1);
        const min = Number(input.min || 1);
        const max = input.max ? min + Math.floor((Number(input.max) - min) / increment) * increment : Infinity;
        const current = Number(input.value || min);
        const next = step.dataset.cartQuantityStep === 'up'
          ? Math.min(max, current + increment)
          : Math.max(min, current - increment);
        if (next === current) return;
        input.value = String(next);
        this.syncQuantityButtons(line);
        this.changeLine(input.dataset.cartKey, next, input.dataset.cartLineIndex, line);
        return;
      }

      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        const line = remove.closest('[data-cart-line]');
        this.changeLine(remove.dataset.cartKey, 0, remove.dataset.cartLineIndex, line);
        return;
      }

      const clear = event.target.closest('[data-cart-clear]');
      if (clear) {
        event.preventDefault();
        const confirmed = window.confirm(text('cartClearConfirm', 'Tem certeza de que deseja remover todos os itens do carrinho?'));
        if (confirmed) this.clearCart();
        return;
      }

      const discount = event.target.closest('[data-cart-discount-apply]');
      if (discount) {
        event.preventDefault();
        this.applyDiscount(discount);
      }
    }

    onChange(event) {
      const quantity = event.target.closest('[data-cart-quantity-input]');
      if (quantity) {
        const line = quantity.closest('[data-cart-line]');
        const min = Number(quantity.min || 1);
        const max = quantity.max ? Number(quantity.max) : Infinity;
        const step = Number(quantity.step || 1);
        let next = Number.parseInt(quantity.value, 10);
        if (!Number.isFinite(next)) next = min;
        next = Math.max(min, Math.min(max, next));
        next = min + Math.floor((next - min) / step) * step;
        quantity.value = String(next);
        this.syncQuantityButtons(line || this.root);
        this.changeLine(quantity.dataset.cartKey, next, quantity.dataset.cartLineIndex, line);
        return;
      }

      const attribute = event.target.closest('[data-cart-attribute]');
      if (attribute) {
        const key = attribute.dataset.cartAttributeKey;
        const value = attribute.type === 'checkbox' ? (attribute.checked ? attribute.value : '') : attribute.value;
        if (key && attribute.type === 'checkbox') this.updateCart({ attributes: { [key]: value } }, false);
        else if (key) this.saveAttribute(key, value);
        return;
      }

      const terms = event.target.closest('[data-cart-terms]');
      if (terms) {
        try {
          sessionStorage.setItem(CART_TERMS_KEY, terms.checked ? '1' : '0');
        } catch { /* Optional storage. */ }
        const message = this.root.querySelector('[data-cart-terms-message]');
        if (message) message.textContent = '';
        this.updateCheckoutState();
      }
    }

    onInput(event) {
      const note = event.target.closest('[data-cart-note]');
      if (note) this.noteSave(note.value);
      const attribute = event.target.closest('[data-cart-attribute][type="text"]');
      if (attribute?.dataset.cartAttributeKey) this.saveAttribute(attribute.dataset.cartAttributeKey, attribute.value);
    }


    onKeydown(event) {
      if (event.key !== 'Enter') return;
      const discountInput = event.target.closest('[data-cart-discount-input]');
      if (discountInput) {
        event.preventDefault();
        const button = this.root.querySelector('[data-cart-discount-apply]');
        if (button && !button.disabled) this.applyDiscount(button);
        return;
      }
      const quantity = event.target.closest('[data-cart-quantity-input]');
      if (quantity) {
        event.preventDefault();
        quantity.blur();
      }
    }

    onSubmit(event) {
      const submitter = event.submitter;
      if (submitter?.name === 'checkout' && !this.validateTerms()) {
        event.preventDefault();
      }
    }
  }

  function init(scope = document) {
    scope.querySelectorAll?.('[data-cart-section]').forEach((root) => {
      if (controllers.has(root)) return;
      const controller = new CartPage(root);
      controllers.set(root, controller);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('urb:cart:updated', () => {
    document.querySelectorAll('[data-cart-section]').forEach((root) => controllers.get(root)?.refreshFromServer());
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-cart-section]').forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  });
})();
